<script lang="ts" module>
    export type Mood = 'idle' | 'focus' | 'brewing' | 'happy' | 'cheer' | 'boom';
</script>

<script lang="ts">
    /**
     * 방울이 조수 — 솥 옆에서 반응한다(작업지시서 5절 · 그림 = 회사 로고 두 벌, 스위티 지시 2026-11-04).
     *
     * 눈 뜬 얼굴(wonder)과 웃는 얼굴(happy) 두 장으로 여섯 가지 반응을 낸다. 터지면 그을리고, 큰 것이 나오면 폴짝폴짝 뛴다.
     * 화면 낭독기에는 읽히지 않는다 — 결과는 결과 칸이 이미 말한다.
     */
    let { mood = 'idle', beat = 0, size = 'normal' }: { mood?: Mood; beat?: number; size?: 'normal' | 'large' } = $props();

    const LINES: Record<Mood, string> = {
        idle: '무엇을 만들어 볼까요?',
        focus: '두근두근…',
        brewing: '보글보글…',
        happy: '해냈어요!',
        cheer: '우와아! 대단해요!',
        boom: '콜록콜록…',
    };

    const face = $derived(mood === 'happy' || mood === 'cheer' ? 'happy' : 'wonder');
</script>

<div class="helper {size}" aria-hidden="true">
    {#key `${mood}-${beat}`}
        <p class="bubble">{LINES[mood]}</p>
        <div class="body mood-{mood}">
            <img src="/images/pop/bangul-{face}.webp" alt="" width="256" height="256" decoding="async" />
            {#if mood === 'boom'}
                <span class="soot"></span>
                <span class="puff">💨</span>
            {/if}
            {#if mood === 'cheer'}
                <span class="spark s1">✨</span>
                <span class="spark s2">✨</span>
                <span class="spark s3">⭐</span>
            {/if}
        </div>
    {/key}
</div>

<style>
    .helper {
        position: relative;
        display: flex;
        flex-direction: column;
        align-items: center;
        width: 5rem;
        flex-shrink: 0;
    }

    .helper.large {
        width: 7rem;
    }

    .bubble {
        margin: 0 0 0.35rem;
        padding: 0.3rem 0.55rem;
        border-radius: 0.8rem;
        background: #fff;
        border: 1px solid rgba(197, 58, 107, 0.25);
        color: #374151;
        font-size: 0.7rem;
        font-weight: 600;
        line-height: 1.3;
        text-align: center;
        word-break: keep-all;
        white-space: nowrap;
        animation: pop-in 0.25s ease-out;
    }

    .large .bubble {
        font-size: 0.85rem;
    }

    .body {
        position: relative;
        width: 100%;
    }

    .body img {
        display: block;
        width: 100%;
        height: auto;
        filter: drop-shadow(0 4px 6px rgba(0, 0, 0, 0.12));
    }

    .mood-idle {
        animation: bob 3s ease-in-out infinite;
    }

    .mood-focus {
        transform-origin: 50% 100%;
        animation: lean 0.6s ease-out forwards;
    }

    .mood-brewing {
        animation: jitter 0.18s linear infinite;
    }

    .mood-happy {
        animation: hop 0.5s ease-out 2;
    }

    .mood-cheer {
        animation: hop 0.45s ease-out infinite;
    }

    .mood-boom {
        animation: wobble 0.5s ease-in-out 2;
    }

    .mood-boom img {
        filter: brightness(0.55) sepia(0.35) drop-shadow(0 4px 6px rgba(0, 0, 0, 0.2));
    }

    /* 그을음 — 얼굴 위에 검은 얼룩 */
    .soot {
        position: absolute;
        inset: 18% 12% 30%;
        border-radius: 50%;
        background: radial-gradient(circle at 35% 40%, rgba(17, 24, 39, 0.55), transparent 45%),
            radial-gradient(circle at 70% 60%, rgba(17, 24, 39, 0.45), transparent 40%);
        pointer-events: none;
    }

    .puff {
        position: absolute;
        top: -0.4rem;
        right: -0.6rem;
        font-size: 1.3rem;
        animation: rise 1.2s ease-out forwards;
    }

    .spark {
        position: absolute;
        font-size: 1rem;
        animation: twinkle 0.9s ease-in-out infinite;
    }

    .s1 {
        top: -0.3rem;
        left: -0.5rem;
    }

    .s2 {
        top: 30%;
        right: -0.7rem;
        animation-delay: 0.3s;
    }

    .s3 {
        bottom: 10%;
        left: -0.6rem;
        animation-delay: 0.6s;
    }

    @keyframes bob {
        0%,
        100% {
            transform: translateY(0);
        }
        50% {
            transform: translateY(-3px);
        }
    }

    @keyframes lean {
        to {
            transform: rotate(-7deg) translateX(-2px);
        }
    }

    @keyframes jitter {
        0% {
            transform: translate(0, 0);
        }
        25% {
            transform: translate(-1px, 1px);
        }
        50% {
            transform: translate(1px, 0);
        }
        75% {
            transform: translate(0, -1px);
        }
    }

    @keyframes hop {
        0%,
        100% {
            transform: translateY(0) scale(1, 1);
        }
        30% {
            transform: translateY(0) scale(1.08, 0.92);
        }
        60% {
            transform: translateY(-14px) scale(0.96, 1.05);
        }
    }

    @keyframes wobble {
        0%,
        100% {
            transform: rotate(0);
        }
        25% {
            transform: rotate(-8deg);
        }
        75% {
            transform: rotate(8deg);
        }
    }

    @keyframes rise {
        from {
            opacity: 1;
            transform: translateY(0) scale(0.8);
        }
        to {
            opacity: 0;
            transform: translateY(-18px) scale(1.3);
        }
    }

    @keyframes twinkle {
        0%,
        100% {
            opacity: 0.2;
            transform: scale(0.7);
        }
        50% {
            opacity: 1;
            transform: scale(1.15);
        }
    }

    @keyframes pop-in {
        from {
            opacity: 0;
            transform: translateY(4px) scale(0.9);
        }
        to {
            opacity: 1;
            transform: none;
        }
    }

    /* 움직임을 줄여 달라는 기기 설정이면 표정만 바꾸고 움직이지 않는다 */
    @media (prefers-reduced-motion: reduce) {
        .body,
        .bubble,
        .puff,
        .spark {
            animation: none !important;
        }
    }

    :global(html.dark) .bubble {
        background: #1f2937;
        color: #f3f4f6;
    }
</style>
